// assets/uc-upsell-modal.js
(() => {
  // Configure the three product handles in snippets/ur-upsell-config.liquid.
  // The modal stays disabled until at least one valid Ultra Rips upsell variant is set.
  const UR = window.UR_UPSELL_CONFIG || {};
  const offers = (Array.isArray(UR.offers) ? UR.offers : [])
    .map(offer => ({
      minPriceCents: Number(offer.minPriceCents || 0),
      maxPriceCents: offer.maxPriceCents == null ? null : Number(offer.maxPriceCents),
      offer: {
        upsellVariantId: Number(offer.upsellVariantId || 0),
        entriesFixed: Number(offer.entriesFixed || 0),
        title: String(offer.title || 'Bonus Upgrade'),
        yesLabel: 'Yes, add my bonus',
        noLabel: 'No thanks, add only my item',
        badgeText: String(offer.badgeText || 'EXCLUSIVE ADD-ON'),
        noteText: String(offer.noteText || 'Ultra Rips collector add-on')
      }
    }))
    .filter(rule => Number.isSafeInteger(rule.offer.upsellVariantId) && rule.offer.upsellVariantId > 0);

  const CONFIG = {
    enabled: offers.length > 0,
    showEveryTime: true,
    sessionStorageKey: 'ur_upsell_seen_session',
    brandName: String(UR.brandName || 'Ultra Rips'),
    logoUrl: typeof UR.logoUrl === 'string' ? UR.logoUrl : '',
    cardBg: '#091832',
    cardBg2: '#13274e',
    textColor: '#f6f9ff',
    mutedTextColor: '#b4c7e7',
    accentColor: '#eb465f',
    accentColor2: '#3d7ce9',
    accentColor3: '#e94458',
    highlightColor: '#ffffff',
    buttonTextColor: '#ffffff',
    badgeBg: '#152d57',
    badgeBorder: '#628fe9',
    badgeText: '#d5e4ff',
    defaultOffer: {
      upsellVariantId: 0,
      entriesFixed: 0,
      title: 'Bonus Upgrade',
      yesLabel: 'Yes, add my bonus',
      noLabel: 'No thanks, add only my item',
      badgeText: 'EXCLUSIVE ADD-ON',
      noteText: 'Ultra Rips collector add-on'
    },
    upsellRules: offers
  };

  let modalEl = null;
  let overlayEl = null;
  let messageEl = null;
  let yesBtn = null;
  let noBtn = null;
  let closeBtn = null;
  let priceNoteEl = null;
  let titleEl = null;
  let badgeEl = null;
  let entriesNumberEl = null;

  let currentAction = null;
  let isProcessing = false;
  let expressClickBound = false;

  const sourceVariantCache = new Map();
  const upsellVariantCache = new Map();

  function submitNativeProductForm(form) {
    if (!(form instanceof HTMLFormElement)) return;

    // Use a true native submit so we bypass this script's submit listener.
    HTMLFormElement.prototype.submit.call(form);
  }

  function isStandardProductFormAction(action) {
    return action && action.type === 'form' && action.form instanceof HTMLFormElement;
  }

  function shouldShowUpsellThisSession() {
    if (CONFIG.showEveryTime) return true;

    try {
      return sessionStorage.getItem(CONFIG.sessionStorageKey) !== '1';
    } catch {
      return true;
    }
  }

  function markUpsellSeenThisSession() {
    try {
      sessionStorage.setItem(CONFIG.sessionStorageKey, '1');
    } catch {}
  }

  function formatEntriesText(raw) {
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return 'extra';
    }

    const str = String(raw).trim();

    if (/^\d+(\.\d+)?$/.test(str)) {
      return new Intl.NumberFormat('en-US').format(Number(str));
    }

    return str;
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  function nextFrame() {
    return new Promise((resolve) => requestAnimationFrame(resolve));
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function forceUnlockPage() {
    document.documentElement.classList.remove('uc-upsell-lock');
    document.body.classList.remove('uc-upsell-lock');
  }

  function getUpsellEntriesFromMap(variantId) {
    const rule = CONFIG.upsellRules.find(x => Number(x.offer.upsellVariantId) === Number(variantId));
    return rule?.offer?.entriesFixed ?? null;
  }

  async function fetchSourceVariantInfo(variantId) {
    const id = Number(variantId || 0);
    if (!id) throw new Error('Missing source variant id.');

    if (sourceVariantCache.has(id)) return sourceVariantCache.get(id);

    const res = await fetch(`/variants/${id}.js`, {
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error('Failed to load source variant.');
    }

    const variant = await res.json();
    sourceVariantCache.set(id, variant);
    return variant;
  }

  async function fetchUpsellVariant(variantId) {
    const id = Number(variantId || 0);
    if (!id) {
      throw new Error('Missing upsell variant id.');
    }

    if (upsellVariantCache.has(id)) return upsellVariantCache.get(id);

    const res = await fetch(`/variants/${id}.js`, {
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error('Failed to load upsell variant.');
    }

    const variant = await res.json();

    const mapped = {
      id: Number(variant.id),
      title: variant.name || variant.public_title || variant.title || 'Special offer',
      priceCents: Number(variant.price || 0),
      priceText: formatMoney(variant.price || 0),
      entriesFixed: getUpsellEntriesFromMap(id)
    };

    upsellVariantCache.set(id, mapped);
    return mapped;
  }

  function ruleMatches(rule, context) {
    const { variantId, priceCents } = context;

    const hasVariantRule = Array.isArray(rule.variantIds) && rule.variantIds.length > 0;
    const hasMinPrice = typeof rule.minPriceCents === 'number';
    const hasMaxPrice = typeof rule.maxPriceCents === 'number';

    const variantOk = !hasVariantRule || rule.variantIds.includes(variantId);
    const minOk = !hasMinPrice || priceCents >= rule.minPriceCents;
    const maxOk = !hasMaxPrice || priceCents <= rule.maxPriceCents;

    return variantOk && minOk && maxOk;
  }

  async function resolveOfferForAction(action) {
    try {
      const sourceVariant = await fetchSourceVariantInfo(action.variantId);
      const priceCents = Number(sourceVariant.price || 0);
      const variantId = Number(action.variantId || 0);
      const rule = CONFIG.upsellRules.find(x => ruleMatches(x, { variantId, priceCents }));
      return rule?.offer || null;
    } catch (err) {
      console.warn('[UR UPSELL] Could not load source variant; using normal add to cart.', err);
      return null;
    }
  }

  function injectStylesIfMissing() {
    if (document.getElementById('uc-upsell-modal-styles')) return;

    const style = document.createElement('style');
    style.id = 'uc-upsell-modal-styles';
    style.textContent = `
      .uc-upsell-modal[hidden] { display: none !important; }

      .uc-upsell-modal {
        --uc-card-bg: ${CONFIG.cardBg};
        --uc-card-bg-2: ${CONFIG.cardBg2};
        --uc-card-text: ${CONFIG.textColor};
        --uc-card-muted: ${CONFIG.mutedTextColor};
        --uc-accent: ${CONFIG.accentColor};
        --uc-accent-2: ${CONFIG.accentColor2};
        --uc-accent-3: ${CONFIG.accentColor3};
        --uc-highlight: ${CONFIG.highlightColor};
        --uc-button-text: ${CONFIG.buttonTextColor};
        --uc-badge-bg: ${CONFIG.badgeBg};
        --uc-badge-border: ${CONFIG.badgeBorder};
        --uc-badge-text: ${CONFIG.badgeText};
        --uc-card-line: color-mix(in srgb, var(--uc-card-text) 13%, transparent);
        --uc-glow-soft: color-mix(in srgb, var(--uc-accent) 24%, transparent);
        --uc-glow-medium: color-mix(in srgb, var(--uc-accent-2) 34%, transparent);

        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: rgba(0, 0, 0, 0.76);
        backdrop-filter: blur(10px) saturate(120%);
        -webkit-backdrop-filter: blur(10px) saturate(120%);
      }

      .uc-upsell-modal__overlay {
        position: absolute;
        inset: 0;
        background:
          radial-gradient(circle at 50% 45%, color-mix(in srgb, var(--uc-accent) 16%, transparent), transparent 38%),
          radial-gradient(circle at 60% 65%, color-mix(in srgb, var(--uc-accent-3) 10%, transparent), transparent 42%);
        pointer-events: auto;
      }

      .uc-upsell-modal__dialog {
  position: relative;
  z-index: 1;
  width: min(100%, 520px);
  border-radius: 22px;
  color: #ffffff;
  overflow: hidden;
  isolation: isolate;
  background-color: #08080d;
  box-shadow:
    0 24px 80px rgba(0,0,0,0.45),
    0 -16px 24px rgba(255,255,255,0.10) inset;
  transform: translateY(8px);
  animation: ucUpsellIn .18s ease-out forwards;
}

/* rotating border layer */
.uc-upsell-modal__dialog::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  z-index: 0;
  background-image: linear-gradient(
    0deg,
    rgba(255,255,255,0.9) -50%,
    rgba(255,255,255,0.14) 50%,
    rgb(245, 197, 66) 100%
  );
  overflow: hidden;
}

/* animated streak constrained to border wrapper */
.uc-upsell-modal__dialog::before {
  box-shadow: none;
}

.uc-upsell-modal__dialog::after {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: 21px;
  z-index: 1;
  background-color: #08080d;
  background-image:
    radial-gradient(at 0% 64%, rgba(22, 200, 255, 0.40) 0px, transparent 72%),
    radial-gradient(at 41% 94%, rgba(22, 200, 255, 0.25) 0px, transparent 72%),
    radial-gradient(at 100% 99%, rgba(22, 200, 255, 0.20) 0px, transparent 72%);
}

/* this creates the rotating border highlight only */
.uc-upsell-modal__dialog > .uc-upsell-modal__border-spin {
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

.uc-upsell-modal__dialog > .uc-upsell-modal__border-spin::before {
  content: '';
  position: absolute;
  width: 180%;
  height: 120px;
  top: 50%;
  left: 50%;
  transform-origin: left;
  background-image: linear-gradient(
    0deg,
    transparent 0%,
    #f5c542 42%,
    #f5c542 58%,
    transparent 100%
  );
  opacity: 0.9;
  animation: ucUpsellBorderRotate 8s linear infinite;
}

@keyframes ucUpsellBorderRotate {
  from {
    transform: rotate(0deg) translate(-50%, -50%);
  }
  to {
    transform: rotate(360deg) translate(-50%, -50%);
  }
}

.uc-upsell-modal__inner {
  position: relative;
  z-index: 2;
  padding: 24px 22px 22px;
}

      .uc-upsell-modal__close {
        position: absolute;
        top: 14px;
        right: 14px;
        z-index: 5;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        padding: 0;
        border: 1px solid color-mix(in srgb, var(--uc-card-text) 22%, transparent);
        border-radius: 999px;
        background: color-mix(in srgb, var(--uc-card-bg-2) 75%, transparent);
        color: var(--uc-card-text);
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--uc-card-text) 16%, transparent);
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
        transition: transform 180ms ease, background 180ms ease;
      }

      .uc-upsell-modal__close:hover {
        transform: rotate(5deg) scale(1.05);
        background: color-mix(in srgb, var(--uc-accent) 25%, var(--uc-card-bg-2));
      }

      .uc-upsell-modal__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin: 0 48px 12px;
        padding: 7px 11px;
        border: 1px solid var(--uc-badge-border);
        border-radius: 999px;
        background: var(--uc-badge-bg);
        color: var(--uc-badge-text);
        font-size: 10px;
        font-weight: 950;
        letter-spacing: .1em;
        text-transform: uppercase;
        backdrop-filter: blur(10px);
      }

      .uc-upsell-modal__badge {
        display: flex;
        width: fit-content;
        margin: 0 auto 12px;
      }

      .uc-upsell-modal__eyebrow {
        margin-bottom: 8px;
        color: var(--uc-card-muted);
        font-size: 11px;
        font-weight: 850;
        letter-spacing: .16em;
        text-transform: uppercase;
        opacity: .5;
      }

      .uc-upsell-modal__title {
        max-width: 440px;
        margin: 0;
        color: var(--uc-card-text);
        font-size: clamp(24px, 5vw, 34px);
        font-weight: 950;
        line-height: 1.06;
        letter-spacing: -.035em;
        text-wrap: balance;
      }

      .uc-upsell-modal__entries {
        position: relative;
        isolation: isolate;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: fit-content;
        margin: 17px auto 15px;
        padding: 10px 16px;
        overflow: hidden;
        border: 1px solid color-mix(in srgb, var(--uc-card-text) 26%, var(--uc-accent));
        border-radius: 999px;
        background:
          linear-gradient(180deg, color-mix(in srgb, var(--uc-card-text) 14%, transparent), transparent 68%),
          linear-gradient(135deg,
            color-mix(in srgb, var(--uc-accent) 34%, var(--uc-card-bg)),
            color-mix(in srgb, var(--uc-accent-2) 20%, var(--uc-card-bg-2))
          );
        box-shadow:
          0 0 22px color-mix(in srgb, var(--uc-card-text) 14%, transparent),
          0 0 26px var(--uc-glow-soft),
          inset 0 1px 0 color-mix(in srgb, var(--uc-card-text) 28%, transparent),
          inset 0 -10px 18px rgba(0,0,0,.2);
      }

      .uc-upsell-modal__entries::before {
        content: '';
        position: absolute;
        z-index: -2;
        inset: -45%;
        border-radius: inherit;
        background: conic-gradient(
          from 0deg,
          transparent 0deg,
          transparent 72deg,
          rgba(255,255,255,.95) 98deg,
          transparent 128deg,
          transparent 360deg
        );
        opacity: .8;
        filter: blur(7px);
        animation: ucUpsellEntriesRotate 3.8s linear infinite;
      }

      .uc-upsell-modal__entries::after {
        content: '';
        position: absolute;
        z-index: -1;
        inset: 2px;
        border-radius: inherit;
        background:
          radial-gradient(circle at 50% 0%, color-mix(in srgb, var(--uc-card-text) 16%, transparent), transparent 48%),
          linear-gradient(135deg,
            color-mix(in srgb, var(--uc-accent) 32%, var(--uc-card-bg)),
            color-mix(in srgb, var(--uc-accent-2) 18%, var(--uc-card-bg-2))
          );
      }

      @keyframes ucUpsellEntriesRotate { to { transform: rotate(360deg); } }

      .uc-upsell-modal__entries-number {
        position: relative;
        z-index: 2;
        color: var(--uc-highlight);
        font-size: 23px;
        font-weight: 1000;
        line-height: 1;
        letter-spacing: -.055em;
        text-shadow:
          0 0 10px color-mix(in srgb, var(--uc-highlight) 42%, transparent),
          0 0 18px rgba(255,255,255,.18);
      }

      .uc-upsell-modal__entries-label {
        position: relative;
        z-index: 2;
        color: var(--uc-card-text);
        font-size: 9px;
        font-weight: 950;
        letter-spacing: .105em;
        line-height: 1;
        text-transform: uppercase;
      }

      .uc-upsell-modal__line {
        width: 100%;
        height: 1px;
        margin-bottom: 16px;
        background: linear-gradient(
          90deg,
          transparent,
          var(--uc-card-line),
          color-mix(in srgb, var(--uc-accent-2) 45%, transparent),
          transparent
        );
      }

      .uc-upsell-modal__message {
        max-width: 455px;
        margin: 0;
        color: var(--uc-card-muted);
        font-size: 15px;
        font-weight: 500;
        line-height: 1.55;
      }

      .uc-upsell-modal__highlight {
        color: var(--uc-card-text);
        font-weight: 900;
      }

      .uc-upsell-modal__offer-price {
        color: var(--uc-highlight);
        font-weight: 1000;
        text-shadow: 0 0 12px color-mix(in srgb, var(--uc-highlight) 30%, transparent);
      }

      .uc-upsell-modal__price-note {
        margin-top: 12px;
        color: var(--uc-card-muted);
        font-size: 11px;
        font-weight: 800;
        letter-spacing: .12em;
        text-transform: uppercase;
        opacity: .62;
      }

      .uc-upsell-modal__actions {
        display: grid;
        grid-template-columns: 1.25fr .9fr;
        gap: 12px;
        width: 100%;
        margin-top: 22px;
      }

      .uc-upsell-modal__btn {
        appearance: none;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 50px;
        padding: 13px 17px;
        border-radius: 9999px;
        font: inherit;
        font-size: 12px;
        font-weight: 950;
        letter-spacing: .04em;
        line-height: 1.2;
        text-transform: uppercase;
        cursor: pointer;
        transition: transform 180ms ease, filter 180ms ease, box-shadow 180ms ease;
      }

      .uc-upsell-modal__btn--yes {
        border: 0;
        background: linear-gradient(0deg, var(--uc-accent) 0%, var(--uc-accent-2) 100%);
        color: var(--uc-button-text);
        box-shadow:
          0 12px 24px rgba(0,0,0,.38),
          0 0 24px var(--uc-glow-soft),
          inset 0 -2px 25px -4px rgba(255,255,255,.75);
      }

      .uc-upsell-modal__btn--yes:hover {
        transform: translateY(-2px);
        filter: brightness(1.08);
        box-shadow:
          0 16px 32px rgba(0,0,0,.5),
          0 0 34px var(--uc-glow-medium),
          inset 0 -2px 25px -4px rgba(255,255,255,.85);
      }

      .uc-upsell-modal__btn--no {
        border: 1px solid color-mix(in srgb, var(--uc-card-text) 17%, transparent);
        background: color-mix(in srgb, var(--uc-card-text) 7%, transparent);
        color: var(--uc-card-muted);
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--uc-card-text) 10%, transparent);
      }

      .uc-upsell-modal__btn--no:hover {
        transform: translateY(-2px);
        color: var(--uc-card-text);
        background: color-mix(in srgb, var(--uc-card-text) 11%, transparent);
      }

      .uc-upsell-modal__btn:active { transform: translateY(0); }
      .uc-upsell-modal__btn:disabled,
      .uc-upsell-modal__close:disabled { cursor: wait; opacity: .7; }

      @media (max-width: 640px) {
        .uc-upsell-modal { padding: 12px; }

        .uc-upsell-modal__dialog {
          width: min(100%, 480px);
          border-radius: 20px;
          box-shadow:
            0 12px 30px rgba(0,0,0,.58),
            0 0 24px var(--uc-glow-soft),
            0 -8px 14px rgba(255,255,255,.07) inset;
        }

        .uc-upsell-modal__surface { border-radius: 19px; }
        .uc-upsell-modal__inner { padding: 24px 17px 18px; }
        .uc-upsell-modal__title { font-size: clamp(23px, 8vw, 30px); }
        .uc-upsell-modal__message { font-size: 14px; }
        .uc-upsell-modal__actions { grid-template-columns: 1fr; }
        .uc-upsell-modal__btn { width: 100%; }
        .uc-upsell-modal__dialog::after { opacity: .45; }
      }

      .uc-upsell-modal__inner {
        position: relative;
        z-index: 2;
        padding: 24px 22px 22px;
        text-align: center;
      }

      .uc-upsell-modal__title,
      .uc-upsell-modal__message {
        margin-left: auto;
        margin-right: auto;
      }

      .uc-upsell-modal__eyebrow,
      .uc-upsell-modal__price-note {
        text-align: center;
      }

      @media (prefers-reduced-motion: reduce) {
        .uc-upsell-modal__dialog,
        .uc-upsell-modal__dialog::after,
        .uc-upsell-modal__entries::before {
          animation: none !important;
        }
      }

      /* Ultra Rips original collector design; no protected character artwork. */
      .uc-upsell-modal {
        background: rgba(2, 8, 24, .80);
        backdrop-filter: blur(12px) saturate(135%);
      }
      .uc-upsell-modal__overlay {
        background:
          radial-gradient(circle at 20% 22%, rgba(232, 63, 87, .16), transparent 36%),
          radial-gradient(circle at 80% 78%, rgba(61, 124, 233, .2), transparent 42%);
      }
      .uc-upsell-modal__dialog {
        width: min(100%, 490px);
        border-radius: 22px;
        border: 1px solid #567bb5;
        background: #0b1b36;
        box-shadow: 0 35px 100px #010817cc, 0 0 0 6px rgba(60,120,218,.12), inset 0 1px #c2d9ff38;
        transform: translateY(16px) perspective(1000px) rotateX(4deg);
        animation: urUpsellFloatIn .28s cubic-bezier(.21,.68,.2,1) forwards;
      }
      @keyframes urUpsellFloatIn {
        to { transform: translateY(0) perspective(1000px) rotateX(0); }
      }
      .uc-upsell-modal__dialog::before {
        inset: 0 0 auto;
        height: 4px;
        border-radius: 0;
        background: linear-gradient(90deg, #ec435b 0 42%, #f6f9ff 49% 51%, #4981e9 58% 100%);
        z-index: 3;
      }
      .uc-upsell-modal__dialog::after {
        inset: 1px;
        border-radius: 21px;
        background:
          radial-gradient(circle at 6% 8%, #e8415e20, transparent 34%),
          radial-gradient(circle at 97% 99%, #4c85e12b, transparent 40%),
          radial-gradient(#b9d3fa12 1px, transparent 1px),
          radial-gradient(#b9d3fa12 1px, transparent 1px),
          linear-gradient(155deg, #10264a, #07162e);
        background-size: auto, auto, 17px 17px, 17px 17px, auto;
        background-position: 0 0, 0 0, 0 0, 8px 8px, 0 0;
      }
      .uc-upsell-modal__inner { padding: 23px 22px 21px; }
      .uc-upsell-modal__eyebrow {
        margin-bottom: 9px;
        color: #bdd2ff;
        opacity: 1;
        letter-spacing: .16em;
      }
      .ur-upsell-logo { display: block; width: auto; height: auto; max-width: 142px; max-height: 40px; margin: auto; object-fit: contain; }
      .uc-upsell-modal__badge {
        border: 1px solid #83a9f1;
        background: linear-gradient(125deg, #183968, #0d2548);
        color: #e2ecff;
        box-shadow: 0 6px 15px #030b1a66, inset 0 1px #e9f1ff35;
        border-radius: 8px;
        transform: skewX(-8deg);
      }
      .uc-upsell-modal__title {
        font-family: "Michroma", var(--font-heading-family, sans-serif);
        font-size: clamp(20px, 5vw, 26px);
        font-weight: 900;
        line-height: 1.2;
        color: #fff;
        letter-spacing: -.03em;
        text-transform: uppercase;
      }
      .uc-upsell-modal__entries {
        display: flex;
        width: 100%;
        min-height: 75px;
        margin: 17px auto 14px;
        border: 1px solid #6486c8;
        border-radius: 14px;
        background: linear-gradient(125deg, #102951, #173765);
        box-shadow: 0 12px 25px #020a1b75, inset 0 1px #c8dbff55;
      }
      .uc-upsell-modal__entries::before { display: none; animation: none; }
      .uc-upsell-modal__entries::after { border-radius: 12px; background: linear-gradient(105deg, #b6d5ff0e, #e8465e0e); }
      .uc-upsell-modal__entries-number { color: #f7faff; font-size: clamp(27px, 7vw, 35px); letter-spacing: -.045em; }
      .uc-upsell-modal__entries-label { color: #bcd5ff; font-size: 11px; }
      .uc-upsell-modal__message { color: #ccdaf6; line-height: 1.55; }
      .uc-upsell-modal__highlight { color: #fff; }
      .uc-upsell-modal__offer-price { color: #f78091; text-shadow: none; }
      .uc-upsell-modal__price-note { opacity: 1; color: #94b5ea; }
      .uc-upsell-modal__line { background: linear-gradient(90deg, transparent, #5b8ee5, #df445d, transparent); }
      .uc-upsell-modal__actions { gap: 10px; grid-template-columns: 1fr; margin-top: 18px; }
      .uc-upsell-modal__btn { border-radius: 12px; min-height: 47px; }
      .uc-upsell-modal__btn--yes {
        border: 1px solid #ff91a1;
        color: white;
        background: linear-gradient(155deg, #f15169, #b92341);
        box-shadow: 0 8px 0 #621b30, 0 14px 22px #02091699, inset 0 1px #ffe3ec88;
      }
      .uc-upsell-modal__btn--no {
        border: 1px solid #5476a8;
        color: #d5e3ff;
        background: linear-gradient(155deg, #1b3869, #122748);
      }
      .uc-upsell-modal__close {
        border: 1px solid #5f82b7;
        background: #11274c;
        color: #d2e4ff;
      }
      @media (max-width: 640px) {
        .uc-upsell-modal__dialog { width: min(100%, 440px); }
        .uc-upsell-modal__inner { padding: 20px 16px 18px; }
        .uc-upsell-modal__title { font-size: clamp(19px, 6.3vw, 24px); }
      }
      @media (prefers-reduced-motion: reduce) {
        .uc-upsell-modal__dialog { animation: none !important; transform: none !important; }
      }

      html.uc-upsell-lock,
      body.uc-upsell-lock { overflow: hidden !important; }
    `;
    document.head.appendChild(style);
  }

  function buildModalMarkup() {
    const wrapper = document.createElement('div');
    wrapper.id = 'uc-upsell-modal';
    wrapper.className = 'uc-upsell-modal';
    wrapper.hidden = true;
    wrapper.innerHTML = `
      <div class="uc-upsell-modal__overlay" data-uc-upsell-overlay></div>
      <div class="uc-upsell-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="uc-upsell-title">
        <div class="uc-upsell-modal__surface" aria-hidden="true"></div>
        <div class="uc-upsell-modal__inner">
          <button type="button" class="uc-upsell-modal__close" id="uc-upsell-close" aria-label="Close">×</button>
          <div class="uc-upsell-modal__badge" id="uc-upsell-badge">${escapeHtml(CONFIG.defaultOffer.badgeText)}</div>
          <div class="uc-upsell-modal__eyebrow">${CONFIG.logoUrl
            ? `<img class="ur-upsell-logo" src="${escapeHtml(CONFIG.logoUrl)}" alt="${escapeHtml(CONFIG.brandName)}" loading="eager">`
            : escapeHtml(CONFIG.brandName)}</div>
          <h2 class="uc-upsell-modal__title" id="uc-upsell-title">${escapeHtml(CONFIG.defaultOffer.title)}</h2>

          <div class="uc-upsell-modal__entries" aria-label="Upsell entries">
            <span class="uc-upsell-modal__entries-number" id="uc-upsell-entries-number">Extra</span>
            <span class="uc-upsell-modal__entries-label">Entries</span>
          </div>

          <div class="uc-upsell-modal__line"></div>
          <p class="uc-upsell-modal__message" id="uc-upsell-message"></p>
          <div class="uc-upsell-modal__price-note" id="uc-upsell-price-note">${escapeHtml(CONFIG.brandName)}</div>

          <div class="uc-upsell-modal__actions">
            <button type="button" class="uc-upsell-modal__btn uc-upsell-modal__btn--yes" id="uc-upsell-yes">${escapeHtml(CONFIG.defaultOffer.yesLabel)}</button>
            <button type="button" class="uc-upsell-modal__btn uc-upsell-modal__btn--no" id="uc-upsell-no">${escapeHtml(CONFIG.defaultOffer.noLabel)}</button>
          </div>
        </div>
      </div>
    `;
    return wrapper;
  }

  function cacheMarkupRefs() {
    modalEl = document.getElementById('uc-upsell-modal');
    overlayEl = modalEl?.querySelector('[data-uc-upsell-overlay]') || null;
    messageEl = document.getElementById('uc-upsell-message');
    yesBtn = document.getElementById('uc-upsell-yes');
    noBtn = document.getElementById('uc-upsell-no');
    closeBtn = document.getElementById('uc-upsell-close');
    priceNoteEl = document.getElementById('uc-upsell-price-note');
    titleEl = document.getElementById('uc-upsell-title');
    badgeEl = document.getElementById('uc-upsell-badge');
    entriesNumberEl = document.getElementById('uc-upsell-entries-number');
  }

  function injectMarkupIfMissing() {
    const existing = document.getElementById('uc-upsell-modal');
    if (existing) existing.remove();
    document.body.appendChild(buildModalMarkup());
    cacheMarkupRefs();
  }

  function setButtonsDisabled(disabled) {
    if (yesBtn) yesBtn.disabled = disabled;
    if (noBtn) noBtn.disabled = disabled;
    if (closeBtn) closeBtn.disabled = disabled;
  }

  function openModal() {
    if (!modalEl) return;
    modalEl.hidden = false;
    document.documentElement.classList.add('uc-upsell-lock');
    document.body.classList.add('uc-upsell-lock');
  }

  function closeModal() {
    if (modalEl) modalEl.hidden = true;
    forceUnlockPage();
  }

  function populateModalForOffer(offer, upsellVariantInfo) {
    if (badgeEl) badgeEl.textContent = offer.badgeText || CONFIG.defaultOffer.badgeText;
    if (titleEl) titleEl.textContent = offer.title || CONFIG.defaultOffer.title;
    if (yesBtn) yesBtn.textContent = offer.yesLabel || CONFIG.defaultOffer.yesLabel;
    if (noBtn) noBtn.textContent = offer.noLabel || CONFIG.defaultOffer.noLabel;

    const entriesFixed =
      upsellVariantInfo?.entriesFixed ??
      offer.entriesFixed ??
      null;

    const formattedEntries = formatEntriesText(entriesFixed);

    if (entriesNumberEl) {
      entriesNumberEl.textContent = formattedEntries;
    }

    if (messageEl) {
      messageEl.innerHTML =
        `Add this collector bonus for <span class="uc-upsell-modal__offer-price">${escapeHtml(upsellVariantInfo.priceText)}</span> and receive <span class="uc-upsell-modal__highlight">${escapeHtml(formattedEntries)} extra entries</span>.`;
    }

    if (priceNoteEl) {
      priceNoteEl.textContent = offer.noteText || `${CONFIG.brandName} special add-on`;
    }
  }

  async function prepareAndShowModal(action) {
    currentAction = action;

    try {
      setButtonsDisabled(true);

      const selectedOffer = action?.selectedOffer || CONFIG.defaultOffer;
      const upsellVariantInfo = await fetchUpsellVariant(selectedOffer.upsellVariantId);

      populateModalForOffer(selectedOffer, upsellVariantInfo);
      openModal();
    } catch (err) {
      console.error('[UC UPSELL] Failed to prepare modal:', err);
      await executeAction(action, false);
    } finally {
      setButtonsDisabled(false);
    }
  }

  async function executeAction(action, includeUpsell) {
    if (!action || isProcessing) return;

    isProcessing = true;
    closeModal();

    if (!CONFIG.showEveryTime) {
      markUpsellSeenThisSession();
    }

    try {
      await nextFrame();

      const selectedUpsellVariantId = Number(action?.selectedOffer?.upsellVariantId || 0);
      const parentVariantId = Number(action.variantId);
      const parentQty = Number(action.quantity || 1);

      await addLinkedItemsToCart({
        parentVariantId,
        parentQty,
        childVariantId: selectedUpsellVariantId,
        includeChild: includeUpsell && !!selectedUpsellVariantId,
        parentProperties: action.properties,
        checkoutMode: action.checkoutMode
      });

      // UCCartDrawerSync.addItems already updates the drawer and bubble.

      if (action.checkoutMode) {
        window.location.href = '/checkout';
        return;
      }
    } catch (err) {
      console.error('[UC UPSELL] executeAction error', err);
      alert(err.message || 'There was a problem adding to cart.');
    } finally {
      currentAction = null;
      isProcessing = false;
      forceUnlockPage();
    }
  }

  async function handleIntercept(action) {
    if (!CONFIG.enabled) {
      await executeAction(action, false);
      return;
    }

    // Subscription / selling-plan products should bypass upsell modal completely
    if (action?.sellingPlanId) {
      submitNativeProductForm(action.form);
      return;
    }

    if (!shouldShowUpsellThisSession()) {
      await executeAction(action, false);
      return;
    }

    const selectedOffer = await resolveOfferForAction(action);

    if (!selectedOffer?.upsellVariantId) {
      await executeAction(action, false);
      return;
    }

    if (CONFIG.upsellRules.some(rule => Number(action.variantId) === Number(rule.offer.upsellVariantId))) {
      await executeAction(action, false);
      return;
    }

    action.selectedOffer = selectedOffer;
    await prepareAndShowModal(action);
  }

  function getMainVariantIdFromForm(form) {
    if (form && form.__variantId) return Number(form.__variantId) || null;
    if (!(form instanceof HTMLFormElement)) return null;

    const fd = new FormData(form);
    const id = fd.get('id');
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function getMainQtyFromForm(form) {
    if (form && form.__quantity) return Number(form.__quantity) || 1;
    if (!(form instanceof HTMLFormElement)) return 1;

    const fd = new FormData(form);
    const qty = Number(fd.get('quantity') || 1);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function buildActionFromForm(form, checkoutMode = false) {
    const variantId = getMainVariantIdFromForm(form);
    const quantity = getMainQtyFromForm(form);
    if (!variantId) return null;
    const fd = new FormData(form);
    const sellingPlanId = Number(fd.get('selling_plan') || 0) || null;
    const properties = {};
    for (const [key, value] of fd.entries()) {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      if (match && typeof value === 'string' && !match[1].startsWith('_uc_bundle_')) {
        properties[match[1]] = value;
      }
    }
    return {
      type: checkoutMode ? 'dynamic_checkout_form' : 'form',
      variantId, quantity, checkoutMode, form, sellingPlanId, properties
    };
  }

  function buildActionFromCustomButton(btn) {
    const variantId = Number(btn?.dataset?.variantId || 0);
    if (!variantId) return null;

    return {
      type: 'custom_button',
      variantId,
      quantity: 1,
      checkoutMode: false
    };
  }

  function bindModalEvents() {
    if (!yesBtn || !noBtn || !closeBtn) return;

    yesBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentAction || isProcessing) return;
      await executeAction(currentAction, true);
    });

    noBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentAction || isProcessing) return;
      await executeAction(currentAction, false);
    });

    closeBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentAction || isProcessing) return;
      await executeAction(currentAction, false);
    });

    if (overlayEl) {
      overlayEl.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!currentAction || isProcessing) return;
        await executeAction(currentAction, false);
      });
    }

    document.addEventListener('keydown', async (e) => {
      if (e.key !== 'Escape' || !currentAction || isProcessing || modalEl?.hidden) return;
      e.preventDefault();
      await executeAction(currentAction, false);
    });
  }

  function shouldInterceptStandardForm(form) {
    if (!form || form.dataset.ucUpsellBound === 'true') return false;
    if (!(form instanceof HTMLFormElement)) return false;
    if (!form.matches('form[action*="/cart/add"]')) return false;
    if (form.closest('[data-ur-no-upsell], [data-promo-upsell-exclusive]')) return false;
    if (form.dataset.urNoUpsell === 'true') return false;
    if (form.querySelector('input[type="file"]')) return false;
    if (new FormData(form).get('selling_plan')) return false;

    const variantId = getMainVariantIdFromForm(form);
    if (!variantId) return false;

    return !!form.querySelector('[type="submit"]');
  }

  function bindStandardForm(form) {
    if (!shouldInterceptStandardForm(form)) return;

    form.dataset.ucUpsellBound = 'true';

    form.addEventListener(
      'submit',
      async (e) => {
        if (form.dataset.ucUpsellBypassOnce === '1') {
          delete form.dataset.ucUpsellBypassOnce;
          return;
        }

        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }
          return;
        }

        const action = buildActionFromForm(form, false);
        if (!action) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        await handleIntercept(action);
      },
      true
    );
  }

  function bindStandardForms(scope = document) {
    const forms = scope.querySelectorAll?.('form[action*="/cart/add"]');
    forms?.forEach(bindStandardForm);
  }

  function shouldInterceptCustomButton(btn) {
    if (!btn || btn.dataset.ucUpsellBound === 'true') return false;
    if (!(btn instanceof HTMLElement)) return false;
    if (!btn.matches('.uc-tabs-card__atc[data-variant-id], [data-variant-id].uc-tabs-card__atc')) return false;

    const variantId = Number(btn.dataset.variantId || 0);
    return !!variantId;
  }

  function bindCustomButton(btn) {
    if (!shouldInterceptCustomButton(btn)) return;

    btn.dataset.ucUpsellBound = 'true';

    btn.addEventListener('click', async (e) => {
      if (isProcessing) return;

      e.preventDefault();
      e.stopPropagation();

      const action = buildActionFromCustomButton(btn);
      if (!action) return;

      const textEl = btn.querySelector('.uc-atc-text');
      const loadingEl = btn.querySelector('.uc-atc-loading');

      try {
        btn.disabled = true;
        if (textEl) textEl.style.display = 'none';
        if (loadingEl) loadingEl.style.display = '';

        await handleIntercept(action);
      } finally {
        btn.disabled = false;
        if (textEl) textEl.style.display = '';
        if (loadingEl) loadingEl.style.display = 'none';
      }
    });
  }

  function bindCustomButtons(scope = document) {
    const buttons = scope.querySelectorAll?.('.uc-tabs-card__atc[data-variant-id], [data-variant-id].uc-tabs-card__atc');
    buttons?.forEach(bindCustomButton);
  }

  function isLikelyExpressTrigger(el) {
    if (!(el instanceof HTMLElement)) return false;

    return !!(
      el.matches('.shopify-payment-button__button') ||
      el.matches('.shopify-payment-button button') ||
      el.matches('shopify-payment-button button') ||
      el.matches('shop-pay-wallet-button') ||
      el.matches('shopify-accelerated-checkout') ||
      el.matches('[data-shopify="payment-button"]') ||
      el.matches('.shopify-payment-button') ||
      el.matches('more-payment-options-link a') ||
      el.closest('.shopify-payment-button') ||
      el.closest('[data-shopify="payment-button"]') ||
      el.closest('shopify-accelerated-checkout') ||
      el.closest('shop-pay-wallet-button') ||
      el.closest('more-payment-options-link')
    );
  }

  function bindShopPayButton(btn) {
    if (!btn || btn.dataset.ucUpsellBound === 'true') return;
    if (!isLikelyExpressTrigger(btn)) return;

    btn.dataset.ucUpsellBound = 'true';

    btn.addEventListener(
      'click',
      async (e) => {
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }
          return;
        }

        const form = btn.closest('form');
        const action = buildActionFromForm(form, true);
        if (!action) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        await handleIntercept(action);
      },
      true
    );
  }

  function bindShopPayButtons(scope = document) {
    const selectors = [
      '.shopify-payment-button__button',
      '.shopify-payment-button button',
      'shopify-payment-button button',
      'shop-pay-wallet-button',
      'shopify-accelerated-checkout',
      '[data-shopify="payment-button"]',
      '.shopify-payment-button',
      'more-payment-options-link a'
    ].join(',');

    const elements = scope.querySelectorAll?.(selectors);
    elements?.forEach(bindShopPayButton);
  }

  function bindGlobalExpressCapture() {
    if (expressClickBound) return;
    expressClickBound = true;

    document.addEventListener(
      'click',
      async (e) => {
        const trigger = e.target.closest(
          '.shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout, shop-pay-wallet-button, more-payment-options-link a, .shopify-payment-button__button'
        );

        if (!trigger || isProcessing) return;

        const form = trigger.closest('form');
        const action = buildActionFromForm(form, true);
        if (!action) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        await handleIntercept(action);
      },
      true
    );
  }

  function observeNewNodes() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          if (node.matches?.('form[action*="/cart/add"]')) bindStandardForm(node);
          if (node.matches?.('.uc-tabs-card__atc[data-variant-id], [data-variant-id].uc-tabs-card__atc')) bindCustomButton(node);

          bindStandardForms(node);
          bindCustomButtons(node);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    if (!CONFIG.enabled || !window.UCCartDrawerSync) return;

    injectStylesIfMissing();
    injectMarkupIfMissing();
    bindModalEvents();

    bindStandardForms();
    bindCustomButtons();
    // Accelerated checkout remains native; intercept standard Add to Cart only.
    observeNewNodes();
  }

  function createBundleGroup() {
    return `uc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  function buildParentProperties(bundleGroup) {
    return {
      _uc_bundle_group: bundleGroup,
      _uc_bundle_role: 'parent'
    };
  }

  function buildChildProperties(bundleGroup, parentVariantId) {
    return {
      _uc_bundle_group: bundleGroup,
      _uc_bundle_role: 'child',
      _uc_parent_variant_id: String(parentVariantId),
      _uc_offer_type: 'upsell'
    };
  }

  async function addLinkedItemsToCart({ parentVariantId, parentQty, parentProperties, childVariantId, includeChild, checkoutMode }) {
    const bundleGroup = createBundleGroup();
    const hasChild = !!(includeChild && Number(childVariantId) && Number(childVariantId) !== Number(parentVariantId));
    const properties = { ...(parentProperties || {}) };
    if (hasChild) Object.assign(properties, buildParentProperties(bundleGroup));

    const parent = {
      id: Number(parentVariantId),
      quantity: Math.max(1, Number(parentQty || 1))
    };
    if (Object.keys(properties).length) parent.properties = properties;
    const items = [parent];
    if (hasChild) {
      items.push({
        id: Number(childVariantId),
        quantity: Math.max(1, Number(parentQty || 1)),
        parent_id: Number(parentVariantId),
        properties: buildChildProperties(bundleGroup, parentVariantId)
      });
    }

    const sync = window.UCCartDrawerSync;
    if (!sync?.addItems) throw new Error('Cart synchronization is unavailable.');
    const result = await sync.addItems(items, { openDrawer: !checkoutMode });

    if (hasChild) {
      try {
        const cart = await sync.getCart();
        const child = (cart.items || []).find(item =>
          item.properties?._uc_bundle_group === bundleGroup &&
          item.properties?._uc_bundle_role === 'child'
        );
        if (child && !child.parent_relationship?.parent_key && !child.parent_relationship?.parent) {
          console.warn('[UR UPSELL] Add-on was not nested; removing unlinked add-on.');
          await sync.changeLine(child.key, 0, { openDrawer: !checkoutMode });
          window.alert('Your main item was added. The bonus add-on could not be linked and was removed.');
        }
      } catch (err) {
        console.warn('[UR UPSELL] Could not verify nested cart relationship:', err);
      }
    }
    return result;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();